import { useEffect } from "react"
import {
  onAuthStateChanged,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signOut as firebaseSignOut,
  type AuthProvider,
} from "firebase/auth"
import { auth, googleProvider, appleProvider } from "../providers/firebase"
import { useAuthStore } from "@/stores/auth-store"
import { getAppleToken } from "@/shared/lib/apple-identity"
import { DEV_BYPASS_AUTH, DEV_BYPASS_USER } from "@/config/dev-bypass"

/**
 * Redirect sign-in does not reliably persist the session on localhost
 * (third-party-cookie / storage partitioning), so use a popup there and keep
 * the redirect flow for production.
 */
const useLocalPopup =
  typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)

function startSignIn(provider: AuthProvider) {
  return useLocalPopup ? signInWithPopup(auth, provider) : signInWithRedirect(auth, provider)
}

export function useAuthListener() {
  const setUser = useAuthStore((s) => s.setUser)

  useEffect(() => {
    // DEV-only: sign in as a fake user and skip Firebase entirely, so the webapp
    // opens without a login. Dead-code-eliminated from production builds.
    if (DEV_BYPASS_AUTH) {
      setUser(DEV_BYPASS_USER)
      return
    }

    // Complete a returning sign-in redirect. The user is also delivered via
    // onAuthStateChanged; this call surfaces any redirect-specific errors.
    getRedirectResult(auth).catch((err) => {
      console.error("Auth redirect error:", err)
      // Prod uses redirect sign-in — a failed attempt surfaces here (not in the
      // useSignIn popup catch), so raise the shared error for the toast.
      useAuthStore.getState().setAuthError()
    })

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          appleToken: getAppleToken(firebaseUser),
        })
      } else {
        const current = useAuthStore.getState().user
        if (!current || current.uid !== "dev-user") {
          setUser(null)
        }
      }
    })
    return unsubscribe
  }, [setUser])
}

export async function signInWithGoogle() {
  return startSignIn(googleProvider)
}

export async function signInWithApple() {
  return startSignIn(appleProvider)
}

export async function signOut() {
  return firebaseSignOut(auth)
}
